import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Chip, Grid, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';

import NativeTourImageDropzone from './NativeTourImageDropzone';
import NativeTourImageField from './NativeTourImageField';
import NativeTourRemoveButton from './NativeTourRemoveButton';

export interface NativeTourListImage {
  svcImageId: string;
  caption: string | null;
}

interface Props {
  value: Array<NativeTourListImage>;
  onChange: (value: Array<NativeTourListImage>) => void;
  max?: number;
  withCaptions?: boolean;
  /** Marks the first image, e.g. as the hero. */
  firstLabel?: string;
  emptyText: string;
}

const UNLIMITED = 100;

/** Images in order: upload, caption, reorder and remove. */
export default function NativeTourImageList({
  value,
  onChange,
  max,
  withCaptions = true,
  firstLabel,
  emptyText,
}: Props) {
  const update = (index: number, change: Partial<NativeTourListImage>) =>
    onChange(value.map((image, i) => (i === index ? { ...image, ...change } : image)));
  const move = (from: number, to: number) => {
    const next = [...value];
    const [image] = next.splice(from, 1);
    next.splice(to, 0, image);
    onChange(next);
  };
  const isFull = max !== undefined && value.length >= max;

  return (
    <Stack spacing={2}>
      {value.length === 0 && <Typography color="text.secondary">{emptyText}</Typography>}
      <Grid container spacing={2}>
        {value.map((image, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, lg: 4 }}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {index + 1}
                  </Typography>
                  {index === 0 && firstLabel && <Chip size="small" color="primary" label={firstLabel} />}
                </Stack>
                <NativeTourImageField
                  label="Image"
                  value={image.svcImageId || null}
                  error={!image.svcImageId}
                  removable={false}
                  onChange={(svcImageId) => update(index, { svcImageId: svcImageId ?? '' })}
                />
                {withCaptions && (
                  <TextField
                    size="small"
                    label="Caption"
                    value={image.caption ?? ''}
                    onChange={(event) => update(index, { caption: event.target.value || null })}
                    fullWidth
                  />
                )}
                <Stack direction="row" justifyContent="flex-end">
                  <Tooltip title="Move earlier">
                    <span>
                      <IconButton
                        aria-label="Move earlier"
                        disabled={index === 0}
                        onClick={() => move(index, index - 1)}
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move later">
                    <span>
                      <IconButton
                        aria-label="Move later"
                        disabled={index === value.length - 1}
                        onClick={() => move(index, index + 1)}
                      >
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <NativeTourRemoveButton
                    label="Remove image"
                    onClick={() => onChange(value.filter((_, i) => i !== index))}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <NativeTourImageDropzone
        room={(max ?? UNLIMITED) - value.length}
        onUploaded={(ids) => onChange([...value, ...ids.map((svcImageId) => ({ svcImageId, caption: null }))])}
      />
      <Box>
        <Button
          startIcon={<AddIcon />}
          disabled={isFull}
          onClick={() => onChange([...value, { svcImageId: '', caption: null }])}
        >
          {isFull ? `Up to ${max} images` : 'Add image'}
        </Button>
      </Box>
    </Stack>
  );
}
